/**
 * 데이터처리(transformLoad) 노드의 소스/타깃 해석 — 속성 패널과 캔버스 노드가 공유한다.
 * 소스/타깃은 속성에서 직접 입력하지 않고 "엣지로 연결된 노드"로 결정된다:
 *   SAM파일(csvData) / DB테이블(rdbData) 이 연결돼 있으면 그것, 아니면 선행/후속 태스크의 내부데이터.
 * 백엔드 canvas_converter 의 nearest_data 와 같은 규칙(다른 처리 노드는 넘어가지 않고, 제어 노드는 투명하게 통과).
 */
import i18n from '../../../i18n';
import { typeLabelOf } from '../nodeCatalog';

export type EndpointKind = 'sam' | 'db' | 'inner';

export interface Endpoint {
  kind: EndpointKind;
  kindLabel: string;          // SAM파일 / DB테이블 / 내부데이터
  node: any;                  // 해석된 노드 (내부데이터면 선행/후속 태스크 노드)
  name: string;               // 식별 명칭 — 파일 경로, 커넥션/테이블, 노드 라벨
  columns: Column[];          // 노드에 이미 담긴 항목 정보 (없으면 빈 배열 — SAM 은 서버에서 조회)
}

export interface Column { name: string; dataType?: string; comment?: string; sample?: string }

/** 끝점 종류 표시 이름 — 부를 때 번역한다 */
export const kindLabelOf = (k: EndpointKind) => (k === 'sam' ? typeLabelOf('csvData') : k === 'db' ? typeLabelOf('rdbData') : String(i18n.t('workflow:endpoint.inner')));

const DATA_TYPES = new Set(['csvData', 'rdbData']);
// 내부데이터·그룹 경계·제어 노드(분기/합류/사용자확인)는 데이터를 바꾸지 않으므로 투명하게 통과 (백엔드 nearest_data·DFD 규칙과 동일)
const PASS_THROUGH = new Set(['innerData', 'group', 'subflow', 'repeatGroup', 'branch', 'merge', 'userConfirm']);

/** 노드가 들고 있는 항목 정보를 Column[] 로 정규화 */
export function nodeColumns(node: any): Column[] {
  const cfg = node?.data?.config || {};
  const raw = cfg.tableColumns;
  const arr: any[] = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.columns) ? raw.columns : []);
  if (arr.length) return arr.map((c: any) => (typeof c === 'string' ? { name: c } : { name: c.name, dataType: c.dataType, comment: c.comment })).filter((c: Column) => c.name);
  const names: any[] = cfg.columns || cfg.dataFields || cfg.extractColumns || cfg.mapping?.map((m: any) => m.target) || [];
  return names.map((c: any) => (typeof c === 'string' ? { name: c } : { name: c?.name })).filter((c: Column) => c.name);
}

export function resolveEndpoint(nodeId: string, direction: 'up' | 'down', nodes: any[], edges: any[]): Endpoint | null {
  const next = (id: string) => edges.filter((e) => (direction === 'up' ? e.target === id : e.source === id)).map((e) => (direction === 'up' ? e.source : e.target));
  return walkEndpoint(next(nodeId), direction, nodes, edges);
}

/**
 * 노드로 들어오는 입력을 **연결선마다** — 조인처럼 입력이 둘 이상인 노드용. 연결선 순서가 곧 왼쪽·오른쪽이다
 * (서버 canvas_converter.direct_inputs 와 같은 순서).
 */
export function resolveInputs(nodeId: string, nodes: any[], edges: any[]): Endpoint[] {
  return edges.filter((e) => e.target === nodeId)
    .map((e) => walkEndpoint([e.source], 'up', nodes, edges))
    .filter((x): x is Endpoint => !!x);
}

/**
 * 내부데이터(또는 처리 노드)가 담는 항목 — 노드에 적힌 것(dataFields 등)이 있으면 그것, 없으면 앞 처리가 내보내는 항목.
 * 예전에는 내부데이터 패널을 열 때 항목을 dataFields 에 자동으로 채워 두었고, 열어 본 적이 없으면 뒤 노드가 「항목 0개」 였다(2026-10-20).
 */
function innerColumns(n: any, nodes: any[], edges: any[], depth = 0): Column[] {
  const own = nodeColumns(n);
  if (own.length || depth > ASSUME_MAX_DEPTH) return own;
  if (n.type !== 'innerData') return producedColumns(n.id, nodes, edges, depth + 1);
  const up = walkEndpoint(edges.filter((e) => e.target === n.id).map((e) => e.source), 'up', nodes, edges);
  if (!up) return [];
  if (up.kind === 'inner') return up.node.id === n.id ? [] : (up.columns.length ? up.columns : producedColumns(up.node.id, nodes, edges, depth + 1));
  return up.columns;
}

function walkEndpoint(start: string[], direction: 'up' | 'down', nodes: any[], edges: any[]): Endpoint | null {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const next = (id: string) => edges.filter((e) => (direction === 'up' ? e.target === id : e.source === id)).map((e) => (direction === 'up' ? e.source : e.target));
  const seen = new Set<string>();
  const frontier = [...start];
  let firstTask: any = null;
  while (frontier.length) {
    const id = frontier.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const n = byId.get(id);
    if (!n) continue;
    if (DATA_TYPES.has(n.type)) {
      const cfg = n.data?.config || {};
      if (n.type === 'csvData') return { kind: 'sam', kindLabel: kindLabelOf('sam'), node: n, name: cfg.filepath || n.data?.label || String(i18n.t('workflow:endpoint.noPath')), columns: nodeColumns(n) };
      return { kind: 'db', kindLabel: kindLabelOf('db'), node: n, name: cfg.table ? `${cfg.connection || '?'} / ${cfg.schema ? `${cfg.schema}.` : ''}${cfg.table}` : (n.data?.label || String(i18n.t('workflow:endpoint.noTable'))), columns: nodeColumns(n) };
    }
    if (PASS_THROUGH.has(n.type)) { frontier.push(...next(id)); if (!firstTask && n.type === 'innerData') firstTask = n; continue; }
    if (n.type === 'memo') continue;
    if (!firstTask) firstTask = n; // 다른 태스크: 넘어가지 않고 내부데이터로 본다
  }
  if (firstTask) return { kind: 'inner', kindLabel: kindLabelOf('inner'), node: firstTask, name: firstTask.data?.label || firstTask.type, columns: innerColumns(firstTask, nodes, edges) };
  return null;
}

/** 캔버스 노드 부제: "SAM파일 → DB테이블 · Move" */
export function summarize(nodeId: string, cfg: any, nodes: any[], edges: any[]): string {
  const s = resolveEndpoint(nodeId, 'up', nodes, edges);
  const tg = resolveEndpoint(nodeId, 'down', nodes, edges);
  const mode = cfg?.transformMode === 'MAPPING' ? i18n.t('workflow:endpoint.mapping', { n: (cfg.mapping || []).length }) : 'Move';
  return `${s ? s.kindLabel : i18n.t('workflow:endpoint.sourceQ')} → ${tg ? tg.kindLabel : i18n.t('workflow:endpoint.targetQ')} · ${mode}${cfg?.mode === 'UPSERT' ? ' · UPSERT' : ''}${cfg?.useBulkLoader ? i18n.t('workflow:endpoint.bulk') : ''}`;
}

/** 레이아웃을 거슬러 올라가는 깊이 한계 — 서로를 가리키는 연결이 있어도 멈춘다 */
const ASSUME_MAX_DEPTH = 5;

/**
 * 데이터처리 노드가 **내보내는** 항목.
 * 항목 매핑이면 매핑의 타깃 항목, 아니면(Move) 소스의 항목을 그대로 내보낸다.
 */
export function producedColumns(nodeId: string, nodes: any[], edges: any[], depth = 0): Column[] {
  if (depth > ASSUME_MAX_DEPTH) return [];
  const n = nodes.find((x) => x.id === nodeId);
  if (!n) return [];
  const cfg = n.data?.config || {};
  if ((cfg.transformMode || '').toUpperCase() === 'MAPPING' && Array.isArray(cfg.mapping) && cfg.mapping.length) {
    return cfg.mapping.map((m: any) => ({ name: m?.target })).filter((c: Column) => c.name);
  }
  const src = resolveEndpoint(nodeId, 'up', nodes, edges);
  if (!src) return [];
  if (src.columns.length) return src.columns;
  if (src.kind === 'sam') return assumedSamColumns(src.node.id, nodes, edges, depth + 1);
  return [];
}

/**
 * **아직 만들어지지 않은 SAM파일**의 항목을 가정한다 — 그 파일에 쓰는 처리 노드가 내보내는 항목.
 *
 * 타깃 SAM파일이 아직 없으면 서버에서 항목을 읽을 수 없어 매핑을 짤 수 없었다. 그런데 그 파일은
 * 이 워크플로가 만들 파일이고, 무엇이 담길지는 앞 노드가 이미 정해 두었다 — 그것을 그대로 쓴다.
 * 그렇게 만든 파일을 다시 소스로 삼는 다음 데이터처리도 같은 가정으로 매핑을 짤 수 있다.
 */
export function assumedSamColumns(samNodeId: string, nodes: any[], edges: any[], depth = 0): Column[] {
  if (depth > ASSUME_MAX_DEPTH) return [];
  const writers = edges.filter((e) => e.target === samNodeId).map((e) => e.source);
  for (const w of writers) {
    const cols = producedColumns(w, nodes, edges, depth + 1);
    if (cols.length) return cols;
  }
  return [];
}
